import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { DragOverlay, useDraggable, useDroppable } from '@dnd-kit/core';
import { CSS } from '@dnd-kit/utilities';
import {
    Planner,
    PlannerWithDetails,
    Todo,
} from '@/common/types/databaseTypes';
import {
    useCreatePlanner,
    usePlannersByDate,
    useUpdatePlanner,
} from '../services/usePlannerQueries';
import { useAuthStore } from '@/stores/authStore';
import { ActionIcon, Stack, Tooltip, Text, Box, Pill } from '@mantine/core';
import { Icon, ICONS } from '@/common/components/Icon';
import { CreateTodoInput } from '@/features/todo/services/todoService';
import { useCreateTodo } from '@/features/todo/services/useTodoQueries';
import { CreatePlannerInput } from '../services/plannerServices';
import {
    BLOCKS_PER_HOUR,
    calculateResizedBlocks,
    blockRangesToIndexArray,
    findContiguousRanges,
    getBlockIndexFromMousePosition,
    HOUR_MARK,
    isBlockAvailable,
    MINUTES_PER_BLOCK,
    TIME_BLOCK_HEIGHT,
    TOTAL_BLOCKS,
    UNSCHEDULED_BLOCK_HEIGHT,
    getBlockFromTime,
    LABEL_WIDTH,
} from '@/common/utils/timeBlockUtils';
import { useStyles } from './TimeBlockContent.styles';
import { TodoPopover } from '@/features/todo/components/todoDrawer/TodoPopover';
import {
    isCalendarTarget,
    isPlannerDragData,
    PlannerDragData,
    TodoDragData,
    useDragStore,
} from '@/stores/dragStore';
import { useCategoryStore } from '@/stores/categoryStore';
import { formatTime } from '@/common/utils/timeBlockFormatters';
import { useCheckWorkspaceMembership } from '@/features/workspace/services/useWorkspaceQueries';

interface TimeBlockContentProps {
    date: Date | null;
    planner?: Partial<Planner> | null;
    isDrawer: boolean;
    targetUserId?: string;
}

/**
 * 빈 공간 드래그 해 새 투두 추가할 때 사용하는 임시 플래너 데이터
 */
interface DraftPlanner {
    blocks: number[];
    todo_title?: string;
    color_name: string;
}

interface DragPreviewProps {
    top: number;
    height: number;
    colorName: string;
    children?: React.ReactNode;
    originalBlocks?: number[];
    newBlocks?: number[];
    isResizing?: boolean;
}

interface DragState {
    /**
     * 드래그 상태
     * idle: 초기 상태
     * adding_time: 시간 추가 중 (selectedPlanner가 있는 상태로 빈 영역을 드래그)
     * adding_todo: 새 할 일 추가 중 (selectedPlanner가 없는 상태로 빈 영역을 드래그)
     * resizing: 시간 조정 중 (selectedPlanner가 있는 상태로, 리사이즈 핸들 이용)
     */
    dragPhase: 'idle' | 'adding_time' | 'adding_todo' | 'resizing';

    /**
     * 드래그 영역의 시작/끝 블록 인덱스
     * startBlock: 드래그 영역의 시작 블록 인덱스
     * endBlock: 드래그 영역의 끝 블록 인덱스
     */
    startBlock: number;
    endBlock: number;

    /**
     * 리사이즈 중인 플래너 ID
     * resizingPlannerId: 리사이즈 중인 플래너 ID
     */
    resizingPlannerId?: string;

    /**
     * 드래그 중인 리사이즈 핸들이 위쪽인지 여부
     * 위쪽/아래쪽 다르게 처리해서
     */
    isStartHandle?: boolean;

    /**
     * 'resizing' 상태에서 handleMouseMove가 실행되었는지 여부 (실제 리사이즈가 일어났는지 확인)
     */
    hasMoved?: boolean;
}

// 플래너 아이템 컬러(mantine theme 사용): background: [0], border: [4], timeString: [9], default: 'red'

export const TimeBlockContent = ({
    date,
    planner: initialPlanner,
    isDrawer,
    targetUserId,
}: TimeBlockContentProps) => {
    const { t } = useTranslation('planner');
    const { theme, classes } = useStyles();
    const { user } = useAuthStore();
    const categories = useCategoryStore.getState().categories;

    const dragStore = useDragStore();
    const {
        activeDragItem,
        occupiedBlocksMap,
        setEnable,
        updateOccupiedBlocks,
        reset: resetDragStore,
    } = dragStore;
    const isOwner = targetUserId === user?.id;

    const updatePlanner = useUpdatePlanner();
    const createTodo = useCreateTodo();
    const createPlanner = useCreatePlanner();

    const [selectedPlanner, setSelectedPlanner] =
        useState<Partial<PlannerWithDetails> | null>(initialPlanner ?? null);
    // const [hoverBlock, setHoverBlock] = useState<number>(-1);
    const [previewBlocks, setPreviewBlocks] = useState<number[]>([]);
    const [draftPlanner, setDraftPlanner] = useState<DraftPlanner>({
        blocks: [],
        todo_title: '',
        color_name: 'red',
    });
    const [dragState, setDragState] = useState<DragState>({
        dragPhase: 'idle',
        startBlock: -1,
        endBlock: -1,
        resizingPlannerId: null,
        isStartHandle: false,
        hasMoved: false,
    });

    // unscheduled list 펼침 여부
    const [isListExpanded, setIsListExpanded] = useState(false);

    // 플래너 아이템 클릭해 여는 투두 팝오버, planner id와 range를 조합한 blockId 값을 사용
    const [openedPopoverId, setOpenedPopoverId] = useState<string | null>(null);
    const openedPopoverIdRef = useRef<string | null>(null); // 현재 시각 표시 타이머에서 상태 체크용으로 사용

    const handlePopoverOpen = (blockId: string) => {
        setOpenedPopoverId(blockId);
        openedPopoverIdRef.current = blockId;
    };

    const handlePopoverClose = () => {
        setOpenedPopoverId(null);
        openedPopoverIdRef.current = null;

        // 현재 시각 타이머 재시작
        updateTimeAndScroll();
        startCurrentTimeTimer();
    };

    // 빈 공간 드래그해 새 투두 추가하는 팝오버
    const [newTodoPopoverOpen, setNewTodoPopoverOpen] = useState(false);

    // 현재 시각 표시를 위한 state, functions
    const [currentTime, setCurrentTime] = useState(new Date());
    const [currentTimeBlock, setCurrentTimeBlock] = useState(-1);
    const [showCurrentTime, setShowCurrentTime] = useState(false);
    const timerIdsRef = useRef<{
        timeoutId: NodeJS.Timeout | null;
        intervalId: NodeJS.Timeout | null;
    }>({ timeoutId: null, intervalId: null }); // useEffect 클린업 함수에서 타이머 제거 시 사용

    // 시간과 스크롤 위치 업데이트 함수
    const updateTimeAndScroll = () => {
        const now = new Date();
        setCurrentTime(now);
        setCurrentTimeBlock(getBlockFromTime(now));
    };

    // 현재 시각 표시 타이머 관리
    const startCurrentTimeTimer = () => {
        // 다음 분 시작까지 남은 시간(밀리초) 계산
        const now = new Date();
        const seconds = now.getSeconds();
        const milliseconds = now.getMilliseconds();
        const timeToNextMinute = (60 - seconds) * 1000 - milliseconds;

        // 첫 번째 업데이트는 다음 분 시작 시점에 맞춰 실행
        const initialTimeoutId = setTimeout(() => {
            if (openedPopoverIdRef.current) {
                // 팝오버가 열려있으면 타임아웃 취소, 다시 그리지 않음 (디스크립션 날아감 방지)
                clearTimeout(initialTimeoutId);
                return;
            }

            // 분이 바뀌는 시점에 상태 업데이트와 스크롤 조정
            updateTimeAndScroll();

            // 이후부터는 정확히 1분(60초) 간격으로 업데이트
            const intervalId = setInterval(() => {
                if (openedPopoverIdRef.current) {
                    // 팝오버가 열려있으면 인터벌 취소, 다시 그리지 않음 (디스크립션 날아감 방지)
                    clearInterval(intervalId);
                    return;
                }

                updateTimeAndScroll();
            }, 60000);

            // 인터벌 아이디 저장
            timerIdsRef.current.intervalId = intervalId;
        }, timeToNextMinute);

        // 타이머 아이디 저장
        timerIdsRef.current.timeoutId = initialTimeoutId;

        // 클린업 함수 반환
        return () => {
            if (timerIdsRef.current.timeoutId) {
                clearTimeout(timerIdsRef.current.timeoutId);
            }
            if (timerIdsRef.current.intervalId) {
                clearInterval(timerIdsRef.current.intervalId);
            }
        };
    };

    const containerRef = useRef<HTMLDivElement>(null);

    // 초기 스크롤 처리
    const scrollAreaRef = useRef<HTMLDivElement>(null);
    // 이전 날짜 값을 추적하는 ref 추가
    const prevDateRef = useRef<Date | null>(null);

    // dragState가 변경될 때마다 ref 업데이트 - handleClickOutside에 사용
    const dragStateRef = useRef(dragState);
    useEffect(() => {
        dragStateRef.current = dragState;
        setEnable(dragState.dragPhase === 'idle'); // resizing/adding 중일 때 dnd 막기
    }, [dragState, setEnable]);

    // 해당 날짜의 플래너 데이터 조회
    const { data: planners, isLoading: isLoadingPlanners } = usePlannersByDate(
        date ?? new Date(),
        targetUserId ?? user?.id ?? ''
    );

    // selectedPlanner가 category 값 갖도록 보장
    useEffect(() => {
        if (selectedPlanner?.id && planners) {
            const plannerWithDetails = planners.find(
                planner => planner.id === selectedPlanner.id
            );
            if (plannerWithDetails && !selectedPlanner.category) {
                setSelectedPlanner(plannerWithDetails);
            }
        }
    }, [selectedPlanner?.id, planners, selectedPlanner?.category]);

    // 초기 스크롤 위치 설정
    useEffect(() => {
        if (!date) return;

        if (isLoadingPlanners) {
            return;
        }

        // 이전 날짜와 현재 날짜가 다른 경우에만 스크롤 위치 조정
        const isSameAsPrevDate =
            prevDateRef.current &&
            date.getDate() === prevDateRef.current.getDate() &&
            date.getMonth() === prevDateRef.current.getMonth() &&
            date.getFullYear() === prevDateRef.current.getFullYear();

        // 이전과 같은 날짜면 스크롤 조정하지 않음
        if (isSameAsPrevDate) return;

        // 이전 날짜 저장
        prevDateRef.current = date;

        // 스크롤 위치 지정 작업을 비동기적으로 수행 (컴포넌트 렌더링 완료 후)
        setTimeout(() => {
            if (!containerRef.current || !scrollAreaRef.current) return;

            const currentDate = new Date();
            const isSameDay =
                date &&
                date.getDate() === currentDate.getDate() &&
                date.getMonth() === currentDate.getMonth() &&
                date.getFullYear() === currentDate.getFullYear();

            let targetBlock = -1;

            // 명확한 케이스 구분을 위한 변수
            let caseType = '';
            let is9amCase = false; // 9시 케이스 여부를 명시적으로 추적

            // 케이스 1: 오늘 날짜인 경우 현재 시간으로 스크롤
            if (isSameDay) {
                targetBlock = getBlockFromTime(currentDate);
                caseType = '현재시간';
            }
            // 케이스 2: 다른 날짜이고 planners가 있는 경우 첫 번째 일정으로 스크롤
            else if (planners && planners.length > 0) {
                let foundPlannerWithBlocks = false;

                // 모든 planner의 blocks를 합쳐서 가장 빠른 시간 찾기
                // flatMap을 사용하여 모든 blocks를 하나의 배열로 합치기
                const allBlocks = planners.flatMap(planner => planner.blocks);

                if (allBlocks.length > 0) {
                    // 모든 blocks 중 가장 빠른(작은) 블록 찾기
                    const sortedBlocks = [...allBlocks].sort((a, b) => a - b);
                    targetBlock = sortedBlocks[0];
                    foundPlannerWithBlocks = true;
                    caseType = '첫번째일정';
                } else {
                    foundPlannerWithBlocks = false;
                }

                // 블록이 있는 플래너를 찾지 못한 경우 9시로 설정 (케이스 3)
                if (!foundPlannerWithBlocks) {
                    targetBlock = 9 * BLOCKS_PER_HOUR;
                    caseType = '9시';
                    is9amCase = true;
                }
            }
            // 케이스 3: 위 두 경우가 아닌 경우 09:00으로 스크롤
            else {
                targetBlock = 9 * BLOCKS_PER_HOUR;
                caseType = '9시';
                is9amCase = true;
            }

            // 스크롤 위치 계산 및 설정
            const scrollTop = targetBlock * TIME_BLOCK_HEIGHT;
            const scrollAreaHeight = scrollAreaRef.current?.clientHeight;

            // 케이스별 스크롤 위치 다르게 적용
            let finalScrollTop: number;

            // 케이스별 스크롤 위치 다르게 적용
            if (caseType === '현재시간') {
                // 케이스 1: 현재 시간이 1/4 정도 지점에 있게 스크롤
                finalScrollTop = Math.max(0, scrollTop - scrollAreaHeight / 4);
            } else {
                // 케이스 2, 3: 첫 번째 일정과 9시는 맨 위에 배치
                finalScrollTop = scrollTop;
            }

            // 스크롤 위치 설정
            if (scrollAreaRef.current) {
                scrollAreaRef.current.scrollTop = finalScrollTop;
            }
        }, 100);
    }, [date, planners, isLoadingPlanners]);

    // 현재 시각 표시 업데이트
    useEffect(() => {
        if (!date) return;

        const currentDate = new Date();
        const isSameDay =
            date.getDate() === currentDate.getDate() &&
            date.getMonth() === currentDate.getMonth() &&
            date.getFullYear() === currentDate.getFullYear();

        setShowCurrentTime(isSameDay);

        if (!isSameDay) return;

        // 현재 시각 초기화
        setCurrentTime(currentDate);
        setCurrentTimeBlock(getBlockFromTime(currentDate));

        const cleanUp = startCurrentTimeTimer();
        return cleanUp;

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [date]);

    // 컴포넌트 마운트 시 occupiedBlocksMap 업데이트
    useEffect(() => {
        if (planners) {
            const newOccupiedBlocksMap = new Map<number, string>();

            planners.forEach(planner => {
                if (planner.blocks && planner.blocks.length > 0) {
                    planner.blocks.forEach(blockIndex => {
                        newOccupiedBlocksMap.set(blockIndex, planner.id);
                    });
                }
            });

            // dragStore 업데이트
            updateOccupiedBlocks(newOccupiedBlocksMap);
        }
    }, [planners, updateOccupiedBlocks]);

    // 컴포넌트 언마운트 시 dragStore 상태 초기화
    useEffect(() => {
        return () => {
            resetDragStore();
        };
    }, [resetDragStore]);

    // 컴포넌트 외부 클릭 감지, selectedPlanner 초기화
    const handleClickOutside = useCallback(
        (e: MouseEvent) => {
            if (
                !initialPlanner && // 특정 플래너를 선택해 들어왔을 경우엔 선택 해제 X
                selectedPlanner &&
                containerRef.current &&
                !containerRef.current.contains(e.target as Node) &&
                dragStateRef.current.dragPhase !== 'adding_time'
            ) {
                setSelectedPlanner(null);
            }
        },
        [initialPlanner, selectedPlanner]
    );

    // 이벤트 등록/해제
    useEffect(() => {
        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [handleClickOutside]); // 핸들러가 변경될 때만 재등록

    const getTimeBlocks = (
        startBlock: number,
        endBlock: number,
        currentPlannerId?: string
    ) => {
        const minBlock = Math.min(startBlock, endBlock);
        const maxBlock = Math.max(startBlock, endBlock);

        // 기본 시간 블록 생성
        const timeBlocks = Array.from(
            { length: maxBlock - minBlock + 1 },
            (_, i) => minBlock + i
        );

        // 가능한 블록만 필터링 (비어있거나, 이 플래너에 이미 저장했던 블록)
        return timeBlocks.filter(block =>
            isBlockAvailable(block, occupiedBlocksMap, currentPlannerId)
        );
    };

    const handlePlannerBlockClick = (
        planner: PlannerWithDetails,
        blockId: string,
        e: React.MouseEvent
    ) => {
        e.stopPropagation();
        if (dragState.dragPhase === 'resizing') return; // 리사이즈 중이면 클릭 이벤트 무시

        if (planner.todo?.id) {
            handlePopoverOpen(blockId);
        }
    };

    const handleResizeStart = ({
        planner,
        startBlock,
        endBlock,
        isStartHandle,
        e,
    }: {
        planner: PlannerWithDetails;
        startBlock: number;
        endBlock: number;
        isStartHandle: boolean; // 방향 체크
        e: React.MouseEvent;
    }) => {
        e.stopPropagation();

        setSelectedPlanner(planner);

        setDragState({
            dragPhase: 'resizing',
            startBlock: startBlock,
            endBlock: endBlock - 1, // [start, end) 형태여서 1을 빼줘야 정확한 endBlock 값임
            resizingPlannerId: planner.id,
            isStartHandle,
            hasMoved: false,
        });
    };

    const handlePlannerUpdate = (plannerUpdates: Partial<Planner>) => {
        const updatedPlanner = {
            // 필요한 필드만 추출
            // ...plannerUpdates,
            id: plannerUpdates.id,
            blocks: plannerUpdates.blocks,
            update_id: user?.id ?? '',
        };

        updatePlanner.mutate(updatedPlanner);
    };

    const handleTimeBlockSelection = (startBlock: number, endBlock: number) => {
        if (!date || !user?.id) return;

        const timeBlocks = getTimeBlocks(
            startBlock,
            endBlock,
            selectedPlanner?.id
        );

        try {
            if (selectedPlanner?.id) {
                // const prevPlanner = selectedPlanner;
                const _blocks =
                    dragState.dragPhase === 'resizing'
                        ? handleResizing({
                              newPosition: endBlock,
                              existingBlocks: selectedPlanner.blocks,
                          })
                        : [
                              ...new Set([
                                  ...selectedPlanner.blocks,
                                  ...timeBlocks,
                              ]),
                          ].sort((a, b) => a - b);

                const _planner = {
                    ...selectedPlanner,
                    blocks: _blocks,
                };

                // 기존 planner update
                setSelectedPlanner(_planner);
                try {
                    handlePlannerUpdate(_planner);
                } catch (e) {
                    setDragState({
                        dragPhase: 'idle',
                        startBlock: -1,
                        endBlock: -1,
                        hasMoved: false,
                    });
                    setNewTodoPopoverOpen(false);
                    setSelectedPlanner(prev => {
                        return {
                            ...prev,
                            blocks: prev?.blocks.filter(
                                (block: number) => !timeBlocks.includes(block)
                            ),
                        };
                    });
                    console.error('Failed to update planner', e);
                }

                if (!isDrawer) {
                    setSelectedPlanner(null);
                } else if (
                    // 특정 투두를 선택해 드로어를 열었을 때는 그 투두의 선택을 계속 유지
                    initialPlanner &&
                    selectedPlanner?.id !== initialPlanner.id
                ) {
                    const _planner = planners.find(
                        p => p.id === initialPlanner.id
                    );
                    setSelectedPlanner(_planner);
                }

                setTimeout(() => {
                    setDragState({
                        dragPhase: 'idle',
                        startBlock: -1,
                        endBlock: -1,
                        hasMoved: false,
                    });
                }, 100);
            } else {
                // 새 planner 생성
                setDraftPlanner({
                    blocks: timeBlocks,
                    color_name: 'red',
                    todo_title: '',
                });

                // setTimeout을 사용해 상태 업데이트를 다음 틱으로 지연시켜
                // 동시에 여러 상태가 변경되어 발생하는 충돌 방지
                setTimeout(() => {
                    setNewTodoPopoverOpen(true);
                    setDragState(prev => ({
                        ...prev,
                        dragPhase: 'adding_todo',
                    }));
                }, 0);
            }
        } catch (e) {
            console.error('Failed to handle time block selection', e);
        }
    };

    const handleResizing = ({
        newPosition,
        existingBlocks,
    }: {
        newPosition: number;
        existingBlocks: number[];
    }) => {
        const { isStartHandle, startBlock } = dragState;

        const { updatedBlocks } = calculateResizedBlocks({
            occupiedBlocksMap: occupiedBlocksMap,
            originalBlocks: existingBlocks,
            startIndexOfTargetRange: startBlock,
            newPosition,
            isStartHandle,
            plannerId: selectedPlanner?.id,
            hasMoved: dragState.hasMoved,
        });

        return updatedBlocks;
    };

    const handleNewTodoSubmit = async (todoData: Partial<Todo>) => {
        if (!draftPlanner || !user?.id) return;

        try {
            // 실제 데이터 생성
            const todoInput: CreateTodoInput = {
                goal_id: todoData?.goal_id,
                title: todoData?.title,
                category_id: todoData?.category_id,
                is_public: todoData?.is_public,
                content: todoData?.content,
                register_id: user.id,
                user_id: targetUserId ?? user.id, // 특정 투두/유저의 플래너일 경우 그 사람의 투두로 저장
            };

            const newTodo = await createTodo.mutateAsync(todoInput);

            const plannerInput: CreatePlannerInput = {
                todo_id: newTodo.id,
                blocks: draftPlanner.blocks,
                selected_date: date,
                register_id: user.id,
                user_id: targetUserId ?? user.id,
            };

            const newPlanner = await createPlanner.mutateAsync(plannerInput);
            // setSelectedPlanner(newPlanner);
        } catch (e) {
            console.error('Failed to create todo and planner', e);
        }
    };

    const handleNewTodoPopoverClose = () => {
        setDraftPlanner(null);

        // setTimeout을 사용해 상태 업데이트를 다음 틱으로 지연시켜
        // 동시에 여러 상태가 변경되어 발생하는 충돌 방지
        setTimeout(() => {
            setNewTodoPopoverOpen(false);

            // 드래그 상태는 마지막에 초기화
            setDragState({
                dragPhase: 'idle',
                startBlock: -1,
                endBlock: -1,
            });
        }, 0);
    };

    const handleMouseDown = (e: React.MouseEvent) => {
        const blockIndex = getBlockIndexFromMousePosition(
            e.clientY,
            containerRef
        );
        if (blockIndex === null || newTodoPopoverOpen) return;

        // 클릭한 위치에 이미 플래너 블록이 있는지 확인
        const occupyingPlannerId = occupiedBlocksMap.get(blockIndex);

        // 플래너 블록이 없을 때만 드래그 시작
        if (!occupyingPlannerId) {
            setDragState({
                dragPhase: 'adding_time',
                startBlock: blockIndex,
                endBlock: blockIndex,
            });
        }
        // 있는 경우 -> plannerBlock의 onClick 실행, handlePlannerBlockClick으로 drawer open 처리
    };

    const handleGridMouseMove = (e: React.MouseEvent) => {
        if (dragState.dragPhase !== 'idle' || newTodoPopoverOpen) {
            handleMouseMove(e);
            return;
        }
    };

    const handleMouseMove = (e: React.MouseEvent) => {
        if (
            (dragState.dragPhase !== 'adding_time' &&
                dragState.dragPhase !== 'resizing') ||
            newTodoPopoverOpen
        )
            return;

        // 드래그를 마친 위치의 블록 인덱스
        const newPosition = getBlockIndexFromMousePosition(
            e.clientY,
            containerRef
        );
        if (newPosition === null) return;

        setDragState(prev => ({
            ...prev,
            endBlock: newPosition,
            hasMoved: true,
        }));

        // 리사이징 중일 때 프리뷰 블록 계산
        if (dragState.dragPhase === 'resizing' && selectedPlanner?.blocks) {
            const { validBlocks } = calculateResizedBlocks({
                occupiedBlocksMap: occupiedBlocksMap,
                originalBlocks: selectedPlanner.blocks,
                startIndexOfTargetRange: dragState.startBlock,
                newPosition,
                isStartHandle: dragState.isStartHandle,
                plannerId: selectedPlanner?.id,
                hasMoved: dragState.hasMoved,
            });

            setPreviewBlocks(validBlocks);
        }
    };

    const handleMouseUp = () => {
        if (
            (dragState.dragPhase !== 'adding_time' &&
                dragState.dragPhase !== 'resizing') ||
            newTodoPopoverOpen
        )
            return;

        handleTimeBlockSelection(dragState.startBlock, dragState.endBlock);
        setPreviewBlocks([]); // 리사이즈 미리보기 블록 초기화
    };

    // 시간 라벨과 타임 블록 그릴 칸(그리드 셀)들 렌더링
    const timeGridCells = Array.from({ length: TOTAL_BLOCKS }, (_, index) => {
        const hour = Math.floor(index / BLOCKS_PER_HOUR);
        const minutes = (index % BLOCKS_PER_HOUR) * MINUTES_PER_BLOCK;
        const showHourMark = minutes === 0;
        const isFirstHourMark = index === 0; // 첫 번째 시간 마크(00:00) 여부 확인

        return (
            <DroppableTimeBlock key={index} timeIndex={index}>
                <div
                    className={
                        showHourMark
                            ? index == 0
                                ? classes.timeGridCellWithHourMarkFirst
                                : classes.timeGridCellWithHourMark
                            : classes.timeGridCell
                    }
                >
                    {showHourMark ? (
                        <div
                            className={
                                isFirstHourMark
                                    ? classes.hourMarkFirst
                                    : classes.hourMark
                            }
                            data-hour-mark="true"
                        >
                            {HOUR_MARK[index]}
                        </div>
                    ) : (
                        <div
                            className={classes.hourMarkBox}
                            data-hour-mark="true"
                        ></div>
                    )}
                </div>
            </DroppableTimeBlock>
        );
    });

    const getBlockPositions = () => {
        const startBlock = Math.min(dragState.startBlock, dragState.endBlock);
        const endBlock = Math.max(dragState.startBlock, dragState.endBlock);

        return {
            top: startBlock * TIME_BLOCK_HEIGHT,
            height: (endBlock - startBlock + 1) * TIME_BLOCK_HEIGHT,
        };
    };

    // 시간 비어있는 플래너 렌더링
    const renderUnscheduledPlanners = () => {
        // if (!planners) return null;

        const unscheduledPlanners = planners?.filter(
            planner => !planner.blocks.length
        );

        // if (!unscheduledPlanners?.length) return null;

        const visiblePlanners =
            isListExpanded || unscheduledPlanners?.length <= 3
                ? unscheduledPlanners
                : unscheduledPlanners?.slice(0, 2);

        return (
            <div
                style={{
                    display: 'flex',
                    alignItems: 'center',
                    borderBottom: `1px solid ${theme.colors.gray[3]}`,
                }}
            >
                {unscheduledPlanners?.length > 3 ? (
                    <div className={classes.unscheduledLabel}>
                        <ActionIcon
                            variant="subtle"
                            color="gray"
                            onClick={() => setIsListExpanded(!isListExpanded)}
                        >
                            <Tooltip label={isListExpanded ? t('timeBlock.collapse') : t('timeBlock.expand')}>
                                <div>
                                    <Icon
                                        name={
                                            isListExpanded
                                                ? ICONS.ARROW.COLLAPSE
                                                : ICONS.ARROW.EXPAND
                                        }
                                        size={14}
                                    />
                                </div>
                            </Tooltip>
                        </ActionIcon>
                    </div>
                ) : (
                    <div className={classes.unscheduledLabel}></div>
                )}
                <Stack
                    gap={0}
                    style={{
                        flex: 1,
                        borderLeft: `1px solid ${theme.colors.gray[3]}`,
                        minHeight: UNSCHEDULED_BLOCK_HEIGHT,
                    }}
                >
                    {/* 드래그 중일 때, 드롭 가능한 영역 표시 */}
                    {activeDragItem && (
                        <DroppableTimeBlock timeIndex={-1}>
                            <div
                                className={classes.unscheduledPlannerContent}
                                style={{
                                    backgroundColor: theme.colors.red[0],
                                }}
                            >
                                <div
                                    className={classes.unscheduledColorBar}
                                    style={{
                                        backgroundColor: theme.colors.red[4],
                                    }}
                                />
                            </div>
                        </DroppableTimeBlock>
                    )}

                    {/* unscheduled planner list */}
                    {visiblePlanners?.map(planner => (
                        <UnscheduledPlannerItem
                            key={planner.id}
                            planner={planner}
                            // onClick={e =>
                            //     handlePlannerBlockClick(planner, planner.id, e)
                            // }
                        />
                    ))}
                    {unscheduledPlanners?.length > 3 && !isListExpanded && (
                        // <div className={classes.dragOverlayItem}>
                        <div
                            role="button"
                            className={classes.unscheduledPlannerContent}
                            onClick={() => setIsListExpanded(!isListExpanded)}
                            style={{
                                cursor: 'pointer',
                                margin: '2px 2px 2px 0',
                            }}
                        >
                            <div
                                className={
                                    classes.expandUnscheduledPlannerButton
                                }
                            >
                                <div className={classes.unscheduledColorBar} />
                                <div className={classes.unscheduledTitle}>
                                    {t('timeBlock.viewMore', { count: unscheduledPlanners?.length - 2 })}
                                </div>
                            </div>
                        </div>
                        // </div>
                    )}
                </Stack>
            </div>
        );
    };

    // 기존 플래너 블록 렌더링
    const renderPlannerBlocks = () => {
        if (!planners) return null;

        return planners.flatMap((planner: PlannerWithDetails) => {
            if (!planner.blocks.length) return [];

            const sortedBlocks = [...planner.blocks].sort((a, b) => a - b);
            const blockRanges: { start: number; end: number }[] = [];

            let rangeStart = sortedBlocks[0];
            let prev = rangeStart;

            for (let i = 1; i <= sortedBlocks.length; i++) {
                const current = sortedBlocks[i];
                if (current != prev + 1) {
                    // 연속되지 않는 구간 발견 -> 이전 범위 저장
                    blockRanges.push({ start: rangeStart, end: prev + 1 });
                    rangeStart = current;
                }
                prev = current;
            }

            // 연속된 블록 범위에 대해 div 생성
            return blockRanges.map(range => {
                const blockId = `${planner.id}-${range.start}-${range.end}`;
                const colorName = planner.category?.color_name ?? 'red';
                // const marginSize = 1; // 위아래 각 1px 여백
                const blockCount = Math.abs(range.end - range.start);
                // const contentHeight =
                //     blockCount * TIME_BLOCK_HEIGHT - marginSize * 2;

                return (
                    <div key={blockId}>
                        <ScheduledPlannerItem
                            planner={planner}
                            range={range}
                            onClick={e =>
                                handlePlannerBlockClick(planner, blockId, e)
                            }
                            handleResizeStart={handleResizeStart}
                        >
                            {openedPopoverId === blockId ? (
                                <TodoPopover
                                    opened={true} // 항상 열린 상태
                                    onClose={() => {
                                        handlePopoverClose();
                                    }}
                                    planner={planner}
                                >
                                    <PlannerBlockContent
                                        planner={planner}
                                        colorName={colorName}
                                        blockCount={blockCount}
                                        range={range}
                                    />
                                </TodoPopover>
                            ) : (
                                <PlannerBlockContent
                                    planner={planner}
                                    colorName={colorName}
                                    blockCount={blockCount}
                                    range={range}
                                />
                            )}
                        </ScheduledPlannerItem>
                    </div>
                );
            });
        });
    };

    // unscheduled
    function UnscheduledPlannerItem({
        planner,
    }: {
        planner: PlannerWithDetails;
    }) {
        const { attributes, listeners, setNodeRef, transform, isDragging } =
            useDraggable({
                id: `${planner.id}::unscheduled`, // planner id에서 사용되지 않을 구분자를 사용
                data: {
                    itemType: 'planner',
                    planner,
                    range: null,
                } as PlannerDragData,
            });

        const style = {
            transform: CSS.Translate.toString(transform),
            opacity: isDragging ? 0.4 : 1,
            cursor: 'grab',
            zIndex: isDragging ? 1000 : 1,
        };

        return (
            <div
                ref={setNodeRef}
                className={classes.dragOverlayItem}
                style={{
                    ...style,
                    borderColor:
                        selectedPlanner?.id === planner.id
                            ? theme.colors[
                                  planner.category?.color_name ?? 'red'
                              ][4]
                            : 'transparent',
                }}
                // 팝오버가 열려있으면 드래그 이벤트를 받지 않음
                {...(!openedPopoverId && attributes)}
                {...(!openedPopoverId && listeners)}
            >
                {openedPopoverId === planner?.id ? (
                    <TodoPopover
                        opened={true}
                        onClose={() => {
                            handlePopoverClose();
                        }}
                        planner={planner}
                    >
                        <div onClick={e => e.stopPropagation()}>
                            <UnscheduledBlockContent planner={planner} />
                        </div>
                    </TodoPopover>
                ) : (
                    <Box
                        style={{ cursor: 'pointer' }}
                        onClick={() => handlePopoverOpen(planner.id)}
                    >
                        <UnscheduledBlockContent planner={planner} />
                    </Box>
                )}
            </div>
        );
    }

    // unscheduled planner block 내부 컨텐츠 ui
    const UnscheduledBlockContent = React.forwardRef<
        HTMLElement, // HTML 요소와 달리 직접 정의한 함수형 컴포넌트에서 ref 사용하려면 이 별도의 처리 필요
        {
            planner: PlannerWithDetails;
        }
    >(({ planner }, ref) => {
        return (
            <article
                ref={ref}
                className={classes.unscheduledPlannerContent}
                style={{
                    backgroundColor:
                        theme.colors[planner.category?.color_name ?? 'red'][0],
                }}
            >
                <div
                    className={classes.unscheduledColorBar}
                    style={{
                        backgroundColor:
                            theme.colors[
                                planner.category?.color_name ?? 'red'
                            ][4],
                    }}
                />
                <div className={classes.unscheduledTitle}>
                    {planner.todo?.title}
                </div>
            </article>
        );
    });
    UnscheduledBlockContent.displayName = 'UnscheduledBlockContent';

    // scheduled block
    function ScheduledPlannerItem({
        planner,
        range,
        onClick,
        children,
        handleResizeStart,
    }: {
        planner: PlannerWithDetails;
        range: { start: number; end: number };
        onClick?: (e: React.MouseEvent) => void;
        children: React.ReactNode;
        handleResizeStart: (args: {
            planner: PlannerWithDetails;
            startBlock: number;
            endBlock: number;
            isStartHandle: boolean;
            e: React.MouseEvent;
        }) => void;
    }) {
        const { data: editPermission, isLoading: permissionLoading } =
            useCheckWorkspaceMembership(planner?.goal?.team_id);

        const isAccessable = editPermission?.isAllowed;

        const { attributes, listeners, setNodeRef, isDragging } = useDraggable({
            id: `${planner.id}::${range.start}::${range.end}`, // planner id에서 사용되지 않을 구분자를 사용
            // id: `scheduled-${planner.id}-${range.start}-${range.end}`,
            data: {
                itemType: 'planner',
                planner,
                range,
            } as PlannerDragData,
        });
        const marginSize = 1; // 위아래 각 1px 여백
        const blockCount = Math.abs(range.end - range.start);
        const contentHeight = blockCount * TIME_BLOCK_HEIGHT - marginSize * 2;

        return (
            <div
                className={classes.plannerBlock}
                ref={setNodeRef}
                // 팝오버가 열려있으면 드래그 이벤트를 받지 않음
                {...(!openedPopoverId && isAccessable && attributes)}
                {...(!openedPopoverId && isAccessable && listeners)}
                style={{
                    position: 'absolute',
                    opacity: isDragging ? 0.5 : 1,
                    cursor: isAccessable ? 'grab' : 'not-allowed',
                    top: range.start * TIME_BLOCK_HEIGHT + marginSize,
                    height: contentHeight,
                }}
                onClick={onClick}
            >
                {/* 상단 리사이즈 핸들 */}
                <div
                    style={{
                        position: 'absolute',
                        top: -4,
                        left: 0,
                        right: 0,
                        height: 8,
                        cursor: isAccessable ? 'n-resize' : 'not-allowed',
                        zIndex: 5,
                    }}
                    onMouseDown={e => {
                        if (isAccessable) {
                            handleResizeStart({
                                planner,
                                startBlock: range.start,
                                endBlock: range.end,
                                isStartHandle: true,
                                e,
                            })
                        }
                    }}
                />
                {/* 하단 리사이즈 핸들 */}
                <div
                    style={{
                        position: 'absolute',
                        bottom: -4,
                        left: 0,
                        right: 0,
                        height: 8,
                        cursor: isAccessable ? 's-resize' : 'not-allowed',
                        zIndex: 5,
                    }}
                    onMouseDown={e => {
                        if (isAccessable) {
                            handleResizeStart({
                                planner,
                                startBlock: range.start,
                                endBlock: range.end,
                                isStartHandle: false,
                                e,
                            })
                        }
                    }}
                />
                {children}
            </div>
        );
    }

    // scheduled planner block 내부 컨텐츠 ui
    const PlannerBlockContent = React.forwardRef<
        HTMLElement, // HTML 요소와 달리 직접 정의한 함수형 컴포넌트에서 ref 사용하려면 이 별도의 처리 필요
        {
            planner: PlannerWithDetails;
            colorName: string;

            blockCount: number;
            range: { start: number; end: number };
        }
    >(({ planner, colorName, blockCount, range }, ref) => {
        return (
            <article
                ref={ref}
                className={classes.plannerArticle}
                style={{
                    borderColor:
                        selectedPlanner?.id === planner.id
                            ? theme.colors[colorName][4]
                            : 'transparent',
                }}
            >
                <div
                    className={classes.plannerContent}
                    style={{
                        backgroundColor: theme.colors[colorName][0],
                    }}
                >
                    {/* 기존 내용 유지 */}
                    <div className={classes.plannerTitleWrapper}>
                        <div
                            className={classes.plannerColorBar}
                            style={{
                                backgroundColor: theme.colors[colorName][4],
                            }}
                        />
                        <div>
                            <p
                                className={
                                    blockCount > 1
                                        ? classes.plannerTitle
                                        : classes.plannerTitleSingle
                                }
                            >
                                {planner.todo?.title}
                            </p>
                            <time
                                className={classes.plannerTime}
                                style={{
                                    color: theme.colors[colorName][9],
                                }}
                            >
                                {formatTime(range.start)}~
                                {formatTime(range.end)}
                            </time>
                        </div>
                    </div>
                </div>
            </article>
        );
    });
    PlannerBlockContent.displayName = 'PlannerBlockContent';

    // 드랍할 위치
    function DroppableTimeBlock({
        timeIndex,
        children,
    }: {
        timeIndex: number;
        children: React.ReactNode;
    }) {
        const { isOver, setNodeRef } = useDroppable({
            id: `time-${timeIndex}`,
            data: { timeIndex },
        });

        const toUnscheduled = timeIndex === -1;

        const isDraggingToCalendar = isCalendarTarget(activeDragItem?.data);
        const isDraggingPlannerData = isPlannerDragData(activeDragItem?.data);

        // 드래그 중인 블록의 높이에 맞게 하이라이트 표시
        const isDraggingScheduled =
            isDraggingPlannerData &&
            (activeDragItem?.data as PlannerDragData).range != null;
        const dragHeight = !isDraggingToCalendar
            ? 0 // 캘린더로 드래그하는 게 아닐 경우 미리보기 X
            : toUnscheduled
              ? 1 // unscheduled 아이템은 1칸만 차지
              : isDraggingScheduled
                ? blockRangesToIndexArray([
                      (activeDragItem?.data as PlannerDragData).range,
                  ]).length // 자신의 range만큼
                : 30 / MINUTES_PER_BLOCK; // default 30분만큼

        const colorName =
            (isDraggingPlannerData
                ? (activeDragItem?.data as PlannerDragData)?.planner?.category
                      ?.color_name
                : (activeDragItem?.data as TodoDragData)?.todo?.category
                      ?.color_name) ?? 'red';

        return (
            <div ref={setNodeRef} style={{ position: 'relative' }}>
                <div>{children}</div>

                {/* 드래그 중일 때만 보이는 하이라이트 오버레이 */}
                {isDraggingToCalendar && isOver && (
                    <div
                        style={{
                            position: 'absolute',
                            top: 0,
                            left: 0,
                            width: '100%',
                            height: dragHeight * TIME_BLOCK_HEIGHT,
                            backgroundColor: theme.fn.rgba(
                                theme.colors[colorName][4],
                                0.2
                            ),
                            zIndex: 5,
                            pointerEvents: 'none', // 마우스 이벤트 통과
                            transition: 'background-color 0.2s, height 0.2s',
                        }}
                    />
                )}
            </div>
        );
    }

    // dragState로 다루는 것의 드래그 프리뷰 (드래그 앤 드랍 X)
    const DragPreview = React.forwardRef<HTMLDivElement, DragPreviewProps>(
        (
            {
                top,
                height,
                colorName,
                children,
                originalBlocks,
                newBlocks,
                isResizing,
            },
            ref
        ) => {
            if (!isResizing || !originalBlocks || !newBlocks) {
                return (
                    <div
                        ref={ref}
                        className={classes.dragPreview}
                        style={{
                            top,
                            height,
                            backgroundColor: theme.colors[colorName][0],
                            border: `2px solid ${theme.colors[colorName][4]}`,
                        }}
                    >
                        {dragState.dragPhase === 'adding_todo' &&
                            draftPlanner.todo_title && (
                                <Text
                                    size={
                                        draftPlanner?.blocks?.length > 1
                                            ? 'xs'
                                            : '10px'
                                    }
                                    style={{
                                        padding: '4px 8px',
                                        whiteSpace: 'nowrap',
                                        overflow: 'hidden',
                                        textOverflow: 'ellipsis',
                                    }}
                                >
                                    {draftPlanner.todo_title}
                                </Text>
                            )}
                        {children}
                    </div>
                );
            }

            // 리사이징 중일 때
            return (
                <>
                    {/* 기존 블록 - 흐릿 */}
                    {findContiguousRanges(originalBlocks).map((range, i) => {
                        const rangeStart = Math.min(...range);
                        const rangeEnd = Math.max(...range);
                        const rangeHeight =
                            (rangeEnd - rangeStart + 1) * TIME_BLOCK_HEIGHT;

                        return (
                            <div
                                key={`original-${i}`}
                                style={{
                                    top: rangeStart * TIME_BLOCK_HEIGHT,
                                    height: rangeHeight,
                                    backgroundColor: theme.fn.rgba(
                                        theme.colors[colorName][0],
                                        0.2
                                    ),
                                    position: 'absolute',
                                    left: 4,
                                    right: 4,
                                    opacity: 0.5,
                                    pointerEvents: 'none',
                                    zIndex: 10,
                                }}
                            ></div>
                        );
                    })}

                    {/* 새 블록 - 선명 */}
                    {findContiguousRanges(newBlocks).map((range, i) => {
                        const rangeStart = Math.min(...range);
                        const rangeEnd = Math.max(...range);
                        const rangeHeight =
                            (rangeEnd - rangeStart + 1) * TIME_BLOCK_HEIGHT;

                        return (
                            <div
                                key={`new-${i}`}
                                ref={i === 0 ? ref : undefined}
                                style={{
                                    top: rangeStart * TIME_BLOCK_HEIGHT,
                                    height: rangeHeight,
                                    backgroundColor: theme.fn.rgba(
                                        theme.colors[colorName][0],
                                        0.2
                                    ),
                                    border: `2px solid ${theme.colors[colorName][4]}`,
                                    position: 'absolute',
                                    left: 4,
                                    right: 4,
                                    pointerEvents: 'none',
                                    zIndex: 11,
                                }}
                            ></div>
                        );
                    })}
                </>
            );
        }
    );
    DragPreview.displayName = 'DragPreview';

    // 현재 시각 표시 컴포넌트
    const renderCurrentTimeIndicator = () => {
        if (!showCurrentTime || currentTimeBlock < 0) return null;

        // 정확한 위치 계산
        const hours = currentTime.getHours();
        const minutes = currentTime.getMinutes();

        // 정확한 위치 계산 (시간 * 블록수 + 분의 비율)
        const hourBlocks = hours * BLOCKS_PER_HOUR; // 시간에 해당하는 블록 수
        const minuteFraction = minutes / MINUTES_PER_BLOCK; // 분에 해당하는 블록 비율
        const exactBlock = hourBlocks + Math.floor(minuteFraction); // 정확한 블록 위치
        const blockOffset = (minuteFraction % 1) * TIME_BLOCK_HEIGHT; // 블록 내 추가 오프셋

        const exactTop = exactBlock * TIME_BLOCK_HEIGHT + blockOffset;
        const timeText = `${hours}:${minutes < 10 ? '0' + minutes : minutes}`; // 분이 한 자리수일 경우 앞에 0 추가

        const OFFSET = 13;

        return (
            <Box
                style={{
                    position: 'absolute',
                    left: `-${OFFSET}px`,
                    right: 0,
                    top: `${exactTop}px`,
                    height: '2px',
                    backgroundColor: theme.colors.red[5],
                    zIndex: 50,
                    pointerEvents: 'none',
                }}
            >
                <Pill
                    style={{
                        position: 'absolute',
                        left: `-${(LABEL_WIDTH + OFFSET) / 2}px`,
                        top: '-8px',
                        backgroundColor: theme.colors.red[5],
                        color: 'white',
                        fontSize: '10px',
                        padding: '0 6px',
                        zIndex: 51,
                    }}
                    size="xs"
                >
                    {timeText}
                </Pill>
            </Box>
        );
    };

    return (
        <>
            {renderUnscheduledPlanners()}
            <div style={{ flex: 1, overflow: 'auto' }} ref={scrollAreaRef}>
                <div
                    ref={containerRef}
                    className={classes.plannerArea}
                    // 드래그 중일 때 다른 요소 선택 방지
                    style={{
                        userSelect:
                            dragState.dragPhase !== 'idle' ? 'none' : 'auto',
                    }}
                    onMouseDown={(e: React.MouseEvent) => {
                        // 팝오버가 열려있는 상태에서는 새로운 드래그 시작 방지
                        if (openedPopoverId !== null || newTodoPopoverOpen)
                            return;

                        if (
                            // 시간 레이블 영역인 경우 이벤트 처리하지 않음
                            !(e.target as Element).closest(
                                '[data-hour-mark="true"]'
                            ) &&
                            // 실제 타임블록 영역 내에서만 handleMouseDown 이벤트 처리
                            (e.currentTarget === e.target ||
                                (e.currentTarget.contains(e.target as Node) &&
                                    !(e.target as Element).closest(
                                        '.mantine-Popover-dropdown'
                                    )))
                        ) {
                            handleMouseDown(e);
                        }
                    }}
                    onMouseMove={handleGridMouseMove}
                    onMouseUp={handleMouseUp}
                    onMouseLeave={handleMouseUp}
                >
                    {timeGridCells}
                    {renderCurrentTimeIndicator()}

                    {/* 새 투두(플래너) 추가 */}
                    {dragState.dragPhase === 'adding_todo' && draftPlanner && (
                        <TodoPopover
                            opened={newTodoPopoverOpen}
                            onClose={handleNewTodoPopoverClose}
                            onSubmit={async (todoData: Partial<Todo>) => {
                                try {
                                    await handleNewTodoSubmit(todoData);
                                    // 성공적으로 투두가 제출된 후에만 팝오버 닫기
                                    handleNewTodoPopoverClose();
                                } catch (error) {
                                    console.error(
                                        'Failed to submit todo:',
                                        error
                                    );
                                    // 에러 발생 시 팝오버는 닫지 않고 유지
                                }
                            }}
                            planner={selectedPlanner}
                            onDataChange={(todoData: Partial<Todo>) => {
                                if (draftPlanner) {
                                    setDraftPlanner({
                                        ...draftPlanner,
                                        todo_title: todoData.title,
                                        color_name:
                                            categories?.find(
                                                c =>
                                                    c.id ===
                                                    todoData.category_id
                                            )?.color_name ?? 'red',
                                    });
                                }
                            }}
                        >
                            <DragPreview
                                {...getBlockPositions()}
                                colorName={draftPlanner?.color_name ?? 'red'}
                            />
                        </TodoPopover>
                    )}

                    {/* block range 조정 */}
                    {dragState.dragPhase === 'resizing' &&
                        selectedPlanner?.blocks && (
                            <DragPreview
                                {...getBlockPositions()}
                                colorName={
                                    selectedPlanner?.category?.color_name ??
                                    'red'
                                }
                                isResizing={true}
                                originalBlocks={selectedPlanner.blocks}
                                newBlocks={previewBlocks}
                            />
                        )}

                    {/* 특정 플래너 선택해서 시간 추가로 그릴 때 */}
                    {dragState.dragPhase === 'adding_time' && (
                        <DragPreview
                            {...getBlockPositions()}
                            colorName={
                                selectedPlanner?.category?.color_name ?? 'red'
                            }
                        />
                    )}

                    {renderPlannerBlocks()}

                    {/* 드래그 앤 드랍 - 드래그하고 있는 요소 보여주기 */}
                    <DragOverlay
                        dropAnimation={null}
                        // modifiers={[snapToTopGrid]}
                    >
                        {activeDragItem ? (
                            <div
                                className={classes.dragOverlayItem}
                                style={{
                                    height:
                                        isPlannerDragData(
                                            activeDragItem.data
                                        ) && activeDragItem.data.range
                                            ? blockRangesToIndexArray([
                                                  activeDragItem.data.range,
                                              ]).length * TIME_BLOCK_HEIGHT
                                            : UNSCHEDULED_BLOCK_HEIGHT,
                                    borderColor:
                                        theme.colors[
                                            (isPlannerDragData(
                                                activeDragItem.data
                                            )
                                                ? activeDragItem.data.planner
                                                : activeDragItem.data.todo
                                            )?.category?.color_name ?? 'red'
                                        ][4],
                                    opacity: 0.7,
                                    zIndex: 1000,
                                    cursor: 'grabbing',
                                    // 중요: transform 원점을 상단 중앙으로 설정
                                    transformOrigin: 'top center',
                                }}
                            >
                                <div
                                    className={
                                        classes.unscheduledPlannerContent
                                    }
                                    style={{
                                        backgroundColor:
                                            theme.colors[
                                                (isPlannerDragData(
                                                    activeDragItem.data
                                                )
                                                    ? activeDragItem.data
                                                          .planner
                                                    : activeDragItem.data.todo
                                                )?.category?.color_name ?? 'red'
                                            ][0],
                                    }}
                                >
                                    <div
                                        className={classes.unscheduledColorBar}
                                        style={{
                                            backgroundColor:
                                                theme.colors[
                                                    (isPlannerDragData(
                                                        activeDragItem.data
                                                    )
                                                        ? activeDragItem.data
                                                              .planner
                                                        : activeDragItem.data
                                                              .todo
                                                    )?.category?.color_name ??
                                                        'red'
                                                ][4],
                                        }}
                                    />
                                    <div className={classes.unscheduledTitle}>
                                        {
                                            (isPlannerDragData(
                                                activeDragItem.data
                                            )
                                                ? activeDragItem.data.planner
                                                      .todo
                                                : activeDragItem.data.todo
                                            )?.title
                                        }
                                    </div>
                                </div>
                            </div>
                        ) : null}
                    </DragOverlay>
                    <div
                        className={classes.dragArea}
                        style={{
                            pointerEvents:
                                dragState.dragPhase !== 'idle'
                                    ? 'auto'
                                    : 'none',
                            userSelect: 'none',
                        }}
                    />
                </div>
            </div>
        </>
    );
};
